import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createEditor, BaseEditor, Descendant } from 'slate';
import { Slate, Editable, withReact, ReactEditor, RenderElementProps } from 'slate-react';

interface TextoEditorProps {
  texto: string;
  onSave: (texto: string) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

// Tipos personalizados para Slate
export type ParagraphElement = { type: 'paragraph'; children: CustomText[] };
export type CustomText = { text: string };
export type CustomElement = ParagraphElement;
declare module 'slate' {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

function htmlToSlate(html: string): Descendant[] {
  if (!html) {
    return [{ type: 'paragraph', children: [{ text: '' }] }];
  }
  try {
    return [{ type: 'paragraph', children: [{ text: html.replace(/<[^>]+>/g, '').replace(/<br\s*\/?>(\r\n|\n|\r)?/g, '\n') }] }];
  } catch (error) {
    console.error('Error al convertir HTML a Slate:', error);
    return [{ type: 'paragraph', children: [{ text: html || '' }] }];
  }
}

function slateToPlainText(value: Descendant[]): string {
  try {
    return value
      .map(n => 'children' in n ? (n.children as CustomText[]).map((c) => c.text).join('') : '')
      .join('\n');
  } catch (error) {
    console.error('Error al convertir Slate a texto plano:', error);
    return '';
  }
}

const TextoEditor: React.FC<TextoEditorProps> = ({
  texto,
  onSave,
  onCancel,
  isLoading = false,
}) => {
  const editor = useMemo(() => withReact(createEditor()), []);
  const [value, setValue] = useState<Descendant[]>(() => {
    try {
      if (!texto) {
        return [{ type: 'paragraph', children: [{ text: '' }] }];
      }
      return htmlToSlate(texto);
    } catch (error) {
      console.error('Error al inicializar el editor:', error);
      return [{ type: 'paragraph', children: [{ text: texto || '' }] }];
    }
  });
  const [currentText, setCurrentText] = useState<string>(texto || '');
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  // Actualizar el texto actual cuando cambia el valor del editor
  useEffect(() => {
    try {
      const plainText = slateToPlainText(value);
      setCurrentText(plainText);
      
      // Verificar si hay cambios reales
      const hasTextChanged = plainText.trim() !== texto.trim();
      setHasChanges(hasTextChanged);
      
      if (hasTextChanged) {
        console.log('TextoEditor - Cambios detectados:', {
          textoOriginal: texto,
          textoActual: plainText
        });
      }
    } catch (error) {
      console.error('Error al convertir el valor del editor a texto plano:', error);
    }
  }, [value, texto]);

  console.log('TextoEditor - Texto inicial:', texto);
  console.log('TextoEditor - Texto actual:', currentText);
  console.log('TextoEditor - Hay cambios:', hasChanges);

  const renderElement = useCallback((props: RenderElementProps) => {
    const { element, attributes, children } = props;
    switch ((element as CustomElement).type) {
      case 'paragraph':
        return <p {...attributes}>{children}</p>;
      default:
        return <p {...attributes}>{children}</p>;
    }
  }, []);

  const handleSave = () => {
    // Solo guardar si hay cambios reales
    if (!hasChanges && currentText === texto) {
      console.log('No hay cambios para guardar, cancelando');
      onCancel();
      return;
    }

    // Verificar si el texto está vacío
    if (!currentText.trim()) {
      console.warn('Intentando guardar texto vacío - se usará un espacio en blanco');
      // Enviar al menos un espacio
      onSave(' ');
      return;
    }

    console.log('TextoEditor - Guardando texto final:', currentText);
    onSave(currentText);
  };

  return (
    <div className="fixed inset-0 bg-foreground/20 backdrop-blur-sm flex items-center justify-center z-[100]">
      <div className="bg-card text-card-foreground rounded-lg shadow-lg w-full max-w-4xl mx-4">
        <div className="p-6">
          <h2 className="text-2xl font-semibold mb-4">Editar Texto</h2>
          <div className="border border-border rounded min-h-[150px] p-2 bg-background">
            <Slate editor={editor} initialValue={value} onChange={setValue}>
              <Editable
                renderElement={renderElement}
                placeholder="Escribe aquí..."
                spellCheck
                autoFocus
                className="min-h-[120px] outline-none"
              />
            </Slate>
          </div>
          <div className="mt-2 text-sm flex justify-between items-center text-muted-foreground">
            <span>{currentText ? `Caracteres: ${currentText.length}` : 'Sin texto'}</span>
            {hasChanges && <span className="text-primary">Cambios no guardados</span>}
          </div>
        </div>
        <div className="px-6 py-4 bg-muted border-t border-border flex justify-end space-x-3 relative z-[101]">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-muted text-foreground rounded-md hover:bg-accent"
            disabled={isLoading}
            type="button"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            className={`px-4 py-2 rounded-md ${hasChanges ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-muted text-foreground hover:bg-accent'}`}
            disabled={isLoading || !hasChanges}
            type="button"
          >
            {isLoading ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TextoEditor; 